import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "@/frontend/contexts/AdminAuthContext";
import { useAdminRole } from "@/backend/hooks/useAdminRole";
import { Loader2 } from "lucide-react";

interface AdminGuardProps {
  children: ReactNode;
}

export const AdminGuard = ({ children }: AdminGuardProps) => {
  const { user, isLoading: authLoading } = useAdminAuth();
  const { data: isAdmin, isLoading: isChecking } = useAdminRole(user, authLoading);

  // Loading state
  if (authLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  // Not an admin - show unauthorized page
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-destructive mb-2">Unauthorized Access</h2>
          <p className="text-muted-foreground mb-4">
            You do not have permission to access this page. Please contact an administrator if you believe this is an error.
          </p>
          <p className="text-sm text-muted-foreground">
            Logged in as: {user.email}
          </p>
        </div>
      </div>
    );
  }

  // All checks passed - render admin content
  return <>{children}</>;
};
